import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Cell, LabelList } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { ChartContainer, ChartTooltip, ChartLegend, ChartConfig } from "../../ui/chart";
import { CHART_MARGIN_TOP, chartStyles } from "../chartStyles";
import { formatNumber } from "../utils";

interface CategoryData {
  key: string;
  displayName: string;
  value: number;
  percentage: number;
}

interface StackedPercentageBarChartProps {
  title: string;
  description?: string;
  focusData: CategoryData[];
  comparisonData: CategoryData[];
  focusYear: number;
  comparisonYear: number;
  descriptionTerm?: string;
}

// Focus year colors using CSS variables (--chart-1 base)
const FOCUS_COLORS = [
  "var(--chart-1)",
  "var(--chart-1-light-1)",
  "var(--chart-1-light-2)",
  "var(--chart-1-light-3)",
  "var(--chart-1-light-4)",
];

// Comparison year colors using CSS variables (--chart-2 base)
const COMPARISON_COLORS = [
  "var(--chart-2)",
  "var(--chart-2-light-1)",
  "var(--chart-2-light-2)",
  "var(--chart-2-light-3)",
  "var(--chart-2-light-4)",
];

export function StackedPercentageBarChart({
  title,
  description,
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  descriptionTerm = "results",
}: StackedPercentageBarChartProps) {
  // Get all unique categories from both years
  const allCategories = new Set<string>();
  focusData.forEach((d) => allCategories.add(d.displayName));
  comparisonData.forEach((d) => allCategories.add(d.displayName));
  const categories = Array.from(allCategories);

  // Calculate totals for each year
  const focusTotal = focusData.reduce((sum, d) => sum + d.value, 0);
  const comparisonTotal = comparisonData.reduce((sum, d) => sum + d.value, 0);

  // Build chart data - one row per year, with count for each category
  const chartData = [
    {
      year: String(focusYear),
      isFocusYear: true,
      total: focusTotal,
      ...Object.fromEntries(
        categories.map((cat) => {
          const entry = focusData.find((d) => d.displayName === cat);
          return [cat, entry?.value ?? 0];
        }),
      ),
    },
    {
      year: String(comparisonYear),
      isFocusYear: false,
      total: comparisonTotal,
      ...Object.fromEntries(
        categories.map((cat) => {
          const entry = comparisonData.find((d) => d.displayName === cat);
          return [cat, entry?.value ?? 0];
        }),
      ),
    },
  ];

  // Build chart config with different colors for each year
  const chartConfig: ChartConfig = Object.fromEntries(
    categories.map((cat, index) => [
      cat,
      {
        label: cat,
        color: FOCUS_COLORS[index % FOCUS_COLORS.length],
      },
    ]),
  );

  // Custom tooltip that shows counts and percentages
  const CustomTooltipContent = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const yearLabel = payload[0]?.payload?.year;
    const isFocusYear = payload[0]?.payload?.isFocusYear;
    const yearData = isFocusYear ? focusData : comparisonData;
    const totalCount = yearData.reduce((sum, d) => sum + d.value, 0);
    const colors = isFocusYear ? FOCUS_COLORS : COMPARISON_COLORS;

    return (
      <div className={chartStyles.tooltipContainer}>
        <p className="tw:font-medium tw:mb-1">{yearLabel}</p>
        {payload.map((entry: any, index: number) => {
          const categoryData = yearData.find((d) => d.displayName === entry.name);
          const count = categoryData?.value ?? 0;
          const percentage =
            categoryData?.percentage ?? (totalCount > 0 ? (count / totalCount) * 100 : 0);
          const categoryIndex = categories.indexOf(entry.name);
          return (
            <div key={index} className={chartStyles.tooltipRow}>
              <div
                className={chartStyles.swatch}
                style={{ backgroundColor: colors[categoryIndex % colors.length] }}
              />
              <span>{entry.name}:</span>
              <span className="tw:font-medium">
                {formatNumber(count)} ({percentage.toFixed(1)}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom legend that shows category names with both color variants
  const CustomLegendContent = () => {
    return (
      <div className={chartStyles.legendContainer}>
        {categories.map((category, index) => (
          <div key={category} className={chartStyles.legendItem}>
            <div className="tw:flex tw:gap-0.5">
              <div
                className={chartStyles.swatch}
                style={{ backgroundColor: FOCUS_COLORS[index % FOCUS_COLORS.length] }}
              />
              <div
                className={chartStyles.swatch}
                style={{ backgroundColor: COMPARISON_COLORS[index % COMPARISON_COLORS.length] }}
              />
            </div>
            <span>{category}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description || `Proportion of ${descriptionTerm} for ${focusYear} vs ${comparisonYear}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="tw:aspect-auto tw:h-[250px] tw:w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: CHART_MARGIN_TOP,
              right: 10,
              left: 10,
              bottom: 10,
            }}
            barCategoryGap="30%"
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="year" tickLine={false} tickMargin={10} axisLine={false} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <ChartTooltip content={<CustomTooltipContent />} />
            {categories.map((category, categoryIndex) => (
              <Bar
                key={category}
                dataKey={category}
                stackId="stack"
                radius={
                  categoryIndex === categories.length - 1
                    ? [4, 4, 0, 0] // Top segment: rounded top
                    : [0, 0, 0, 0] // Other segments: no rounding
                }
              >
                {chartData.map((entry, dataIndex) => (
                  <Cell
                    key={`cell-${dataIndex}`}
                    fill={
                      entry.isFocusYear
                        ? FOCUS_COLORS[categoryIndex % FOCUS_COLORS.length]
                        : COMPARISON_COLORS[categoryIndex % COMPARISON_COLORS.length]
                    }
                  />
                ))}
                {categoryIndex === categories.length - 1 && (
                  <LabelList
                    dataKey="total"
                    position="top"
                    formatter={(value: number) => formatNumber(value)}
                    className="tw:fill-foreground tw:text-sm tw:font-medium"
                  />
                )}
              </Bar>
            ))}
            <ChartLegend content={<CustomLegendContent />} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
